import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import { canEditMobileHome, hasAdminPageAccess } from 'utils/roles'

import ApplicationRoutes from './pages/applications/routes'
import TruckHistoryRoutes from './pages/truck-history/routes'
import NotificationLogsRoutes from './pages/notification-logs/routes'
import InvoiceReportsPage from './pages/invoice-reports'
import TelegramLogin from 'pages/telegram-watcher/TelegramWatcher'
import DNSMonitoringRoutes from './pages/dns-monitoring/routes'
import EtaHistoryRoutes from './pages/eta-history/routes'
import EldIntegrationRequestsRoutes from './pages/eld-integration-requests/routes'
import McVerificationRoutes from './pages/mc-verification/routes'
import BoxTruckBidAnalyticsRoutes from './pages/box-truck-bid-analytics/routes'
import BoxTruckPricingAnalyticsRoutes from './pages/box-truck-pricing-analytics/routes'
import DispatcherBidsRoutes from './pages/dispatcher-bids/routes'
import DriverBidsRoutes from './pages/driver-bids/routes'
import BoxTruckZipAnalyticsRoutes from './pages/box-truck-zip-analytics/routes'
import BoxTruckOfferAnalyticsRoutes from './pages/box-truck-offer-analytics/routes'
import BoxTruckDriversRoutes from './pages/box-truck-drivers/routes'
import AgentActivityLogsRoutes from './pages/agent-activity-logs/routes'
import DocumentJobsRoutes from './pages/document-jobs/routes'
import DocumentDevicesRoutes from './pages/document-devices/routes'
import DocumentPipelineAuditRoutes from './pages/document-pipeline-audit/routes'
import DocumentProcessingTestRoutes from './pages/document-processing-test/routes'
import DatTokenUsageRoutes from './pages/dat-token-usage/routes'
import DatTokenProtectionRoutes from './pages/dat-token-protection/routes'
import BillingRoutes from './pages/billing/routes'
import SystemConfig from './pages/system-config/SystemConfig'
import OperatorsPage from './pages/operators/OperatorsPage'
import PaymentLinksPage from './pages/payment-links/PaymentLinksPage'
import ProxyAllowlistPage from './pages/proxy-allowlist/ProxyAllowlistPage'
import VpnUsersPage from './pages/vpn-users/VpnUsersPage'
import PromoCodesPage from './pages/promo-codes/PromoCodesPage'
import LiveDatSearchPage from './pages/live-dat-search/LiveDatSearchPage'
import MobileVersionPolicyPage from './pages/mobile-version-policy/MobileVersionPolicyPage'
import ExtensionVersionPolicyPage from './pages/extension-version-policy/ExtensionVersionPolicyPage'
import InstantPayFeePage from './pages/instant-pay-fee/InstantPayFeePage'
import MobileFeaturesPage from './pages/mobile-features/MobileFeaturesPage'
import MobileHomeLayoutPage from './pages/mobile-home-layout/MobileHomeLayoutPage'
import MobilePromoPagesPage from './pages/mobile-promo-pages/MobilePromoPagesPage'
import MobileLoadAccessPage from './pages/mobile-load-access/MobileLoadAccessPage'
import MobileLoadSortPage from './pages/mobile-load-sort/MobileLoadSortPage'
import MobilePartnerOffersPage from './pages/mobile-partner-offers/MobilePartnerOffersPage'
import MobileRateCheckPage from './pages/mobile-rate-check/MobileRateCheckPage'
import MobileOtaUpdatesPage from './pages/mobile-ota-updates/MobileOtaUpdatesPage'
import PushNotificationsPage from './pages/push-notifications/PushNotificationsPage'
import GrowthCampaignPage from './pages/growth-campaign/GrowthCampaignPage'
import SearchCohortsPage from './pages/search-cohorts/SearchCohortsPage'
import SchedulersPage from './pages/schedulers/SchedulersPage'

// Protected route wrapper for admin-level page access
// In production: Check for gluon_admin or role_gluon_pm
// In development: Allow access to everyone
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    if (!hasAdminPageAccess()) {
        return <Navigate to="/truck-history" replace />
    }

    return <>{children}</>
}

function MobileHomeEditorRoute({ children }: { children: React.ReactNode }) {
    if (!canEditMobileHome()) {
        return <Navigate to="/truck-history" replace />
    }

    return <>{children}</>
}

export default function DashboardRoutes() {
    const { isAuthenticated } = useAuth()

    if (!isAuthenticated) {
        return <Navigate to="/auth/login" replace />
    }

    // Determine default redirect based on environment and role
    const defaultRedirect = hasAdminPageAccess() ? 'applications' : 'truck-history'

    return (
        <Routes>
            <Route index element={<Navigate to={defaultRedirect} replace />} />
            <Route
                path="applications/*"
                element={
                    <ProtectedRoute>
                        <ApplicationRoutes />
                    </ProtectedRoute>
                }
            />
            <Route path="truck-history/*" element={<TruckHistoryRoutes />} />
            <Route path="notification-logs/*" element={<NotificationLogsRoutes />} />
            <Route path="invoice-reports" element={<InvoiceReportsPage />} />
            <Route path="eta-history/*" element={<EtaHistoryRoutes />} />
            <Route path="telegram-watcher" element={<TelegramLogin />} />
            <Route path="dns-monitoring/*" element={<DNSMonitoringRoutes />} />
            <Route path="agent-activity-logs/*" element={<AgentActivityLogsRoutes />} />
            <Route path="document-pipeline/audit/*" element={<DocumentPipelineAuditRoutes />} />
            <Route path="document-pipeline/document-jobs/*" element={<DocumentJobsRoutes />} />
            <Route path="document-pipeline/document-processing-test/*" element={<DocumentProcessingTestRoutes />} />
            <Route path="document-pipeline/devices/*" element={<DocumentDevicesRoutes />} />
            <Route
                path="dat-token-usage/*"
                element={
                    <ProtectedRoute>
                        <DatTokenUsageRoutes />
                    </ProtectedRoute>
                }
            />
            <Route
                path="dat-token-protection/*"
                element={
                    <ProtectedRoute>
                        <DatTokenProtectionRoutes />
                    </ProtectedRoute>
                }
            />
            <Route path="eld-integration-requests/*" element={<EldIntegrationRequestsRoutes />} />
            <Route
                path="mc-verification/*"
                element={
                    <ProtectedRoute>
                        <McVerificationRoutes />
                    </ProtectedRoute>
                }
            />
            {/* Open to every control-panel operator (read-only cross-tenant view) — no admin gate. */}
            <Route path="box-truck-drivers/*" element={<BoxTruckDriversRoutes />} />
            <Route path="box-truck-bid-analytics/*" element={<BoxTruckBidAnalyticsRoutes />} />
            <Route path="box-truck-pricing-analytics/*" element={<BoxTruckPricingAnalyticsRoutes />} />
            <Route path="dispatcher-bids/*" element={<DispatcherBidsRoutes />} />
            <Route path="driver-bids/*" element={<DriverBidsRoutes />} />
            <Route path="box-truck-zip-analytics/*" element={<BoxTruckZipAnalyticsRoutes />} />
            <Route path="box-truck-offer-analytics/*" element={<BoxTruckOfferAnalyticsRoutes />} />
            <Route
                path="billing/*"
                element={
                    <ProtectedRoute>
                        <BillingRoutes />
                    </ProtectedRoute>
                }
            />
            <Route
                path="payment-links"
                element={
                    <ProtectedRoute>
                        <PaymentLinksPage />
                    </ProtectedRoute>
                }
            />
            {/* Open to every gluon operator (not admin-gated) — like the operators page.
                The backend proxy-allowlist router still enforces requireGluonOperator, so raw
                carrier tokens can't reach it. */}
            <Route path="proxy-allowlist" element={<ProxyAllowlistPage />} />
            <Route path="proxy-allowlist/vpn-users" element={<VpnUsersPage />} />
            <Route
                path="promo-codes"
                element={
                    <ProtectedRoute>
                        <PromoCodesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="system-config"
                element={
                    <ProtectedRoute>
                        <SystemConfig />
                    </ProtectedRoute>
                }
            />
            {/* Open to every gluon user (not admin-gated) so any Numeo operator can manage the list. */}
            <Route path="operators" element={<OperatorsPage />} />
            <Route path="schedulers" element={<SchedulersPage />} />
            <Route
                path="live-dat-search"
                element={
                    <ProtectedRoute>
                        <LiveDatSearchPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-version-policy"
                element={
                    <ProtectedRoute>
                        <MobileVersionPolicyPage />
                    </ProtectedRoute>
                }
            />
            {/* Open to every gluon user (not admin-gated) so any Numeo operator can set the extension version. */}
            <Route path="extension-version-policy" element={<ExtensionVersionPolicyPage />} />
            <Route
                path="instant-pay-fee"
                element={
                    <ProtectedRoute>
                        <InstantPayFeePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-features"
                element={
                    <ProtectedRoute>
                        <MobileFeaturesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-home-layout"
                element={
                    <MobileHomeEditorRoute>
                        <MobileHomeLayoutPage />
                    </MobileHomeEditorRoute>
                }
            />
            <Route
                path="mobile-promo-pages"
                element={
                    <MobileHomeEditorRoute>
                        <MobilePromoPagesPage />
                    </MobileHomeEditorRoute>
                }
            />
            <Route
                path="mobile-load-access"
                element={
                    <ProtectedRoute>
                        <MobileLoadAccessPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-load-sort"
                element={
                    <ProtectedRoute>
                        <MobileLoadSortPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-partner-offers"
                element={
                    <ProtectedRoute>
                        <MobilePartnerOffersPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-ota-updates"
                element={
                    <ProtectedRoute>
                        <MobileOtaUpdatesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="mobile-rate-check"
                element={
                    <ProtectedRoute>
                        <MobileRateCheckPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="push-notifications"
                element={
                    <ProtectedRoute>
                        <PushNotificationsPage />
                    </ProtectedRoute>
                }
            />
            {/* Open to every gluon operator (not admin-gated) — the backend growth-campaign
                router still enforces requireGluonOperator, so raw carrier tokens can't write it. */}
            <Route path="growth-campaign" element={<GrowthCampaignPage />} />
            <Route
                path="search-cohorts"
                element={
                    <ProtectedRoute>
                        <SearchCohortsPage />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    )
}
